import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Mail, Plus, Snowflake, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { Field } from "../components/fields";
import DateInput from "../components/DateInput";

const EMPTY_FZ = { query: "", action: "", action_date: "", notes: "", status: "abierto", result: "activated", matched_names: [], deteccion: false, inmovilizacion: false, reporte_autoridad: false, confidencialidad: false };

export default function FreezeCases() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FZ);
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState("");

  const load = () => api.get("/compliance/freeze-cases").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setForm({ ...EMPTY_FZ, query: c.query, action: c.action || "", action_date: c.action_date || "", notes: c.notes || "", status: c.status || "abierto", result: c.result || "activated", matched_names: c.matched_names || [], deteccion: !!c.deteccion, inmovilizacion: !!c.inmovilizacion, reporte_autoridad: !!c.reporte_autoridad, confidencialidad: !!c.confidencialidad });
    setEditing(c);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (adding) {
        await api.post("/compliance/freeze-cases", form);
        toast.success(t("fz.created"));
      } else {
        await api.patch(`/compliance/freeze-cases/${editing.id}`, form);
        toast.success(t("fz.updated"));
      }
      setEditing(null); setAdding(false); load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(t("fz.delete_confirm"))) return;
    try {
      await api.delete(`/compliance/freeze-cases/${c.id}`);
      toast.success(t("fz.deleted"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sendEmail = async (c) => {
    setEmailBusy(c.id);
    try {
      const { data } = await api.post(`/compliance/freeze-cases/${c.id}/email`, {});
      toast.success(`${t("search.email_sent")} ${data.to}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEmailBusy("");
    }
  };

  const statusCls = { abierto: "bg-red-50 text-red-700", documentado: "bg-emerald-50 text-emerald-700", cerrado: "bg-slate-100 text-slate-600" };

  return (
    <div className="space-y-6" data-testid="freeze-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
          <Snowflake className="w-7 h-7 text-[#044CBC]" /> {t("fz.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t("fz.subtitle")}</p>
      </div>
      <div className="flex justify-end">
        <button onClick={() => downloadFile(`/export/compliance.pdf?kind=freeze&lang=${lang}`, "congelamiento_kycfederal.pdf")} data-testid="freeze-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#16335F]">
          <Download className="w-4 h-4" /> {t("common.export_pdf")}
        </button>
        <button onClick={() => downloadFile("/export/compliance.xlsx?kind=freeze", "congelamiento_kycfederal.xlsx")} data-testid="freeze-export-excel" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
          <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
        </button>
        <button onClick={() => { setForm(EMPTY_FZ); setAdding(true); }} data-testid="freeze-add-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C]">
          <Plus className="w-4 h-4" /> {t("fz.add")}
        </button>
      </div>

      <div className="ec-card overflow-hidden" data-testid="freeze-table">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="freeze-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("fz.case")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("fz.query")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("fz.matched")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.result")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("fz.status")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`freeze-row-${c.case_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{c.case_number}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{c.query}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{c.matched_names?.length ? c.matched_names.join(", ") : "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${c.result === "activated" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`freeze-result-${c.case_number}`}>
                        {c.result === "activated" ? t("fz.activated") : t("fz.no_match")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusCls[c.status] || statusCls.documentado}`}>{t(`fz.${{ abierto: "open", documentado: "documented", cerrado: "closed" }[c.status] || "documented"}`)}</span></td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-2.5 text-right space-x-3">
                      <button onClick={() => openEdit(c)} data-testid={`freeze-edit-${c.case_number}`} className="text-slate-600 hover:text-slate-900 text-xs font-bold">{t("fz.update")}</button>
                      <button onClick={() => downloadFile(`/compliance/freeze-cases/${c.id}/pdf?lang=${lang}`, `${c.case_number}.pdf`)} data-testid={`freeze-pdf-${c.case_number}`} className="text-[#044CBC] hover:text-[#033A8C] text-xs font-bold inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />PDF</button>
                      <button onClick={() => sendEmail(c)} disabled={emailBusy === c.id} data-testid={`freeze-email-${c.case_number}`} className="text-[#044CBC] hover:text-[#033A8C] text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60">
                        {emailBusy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}{t("search.send_email")}
                      </button>
                      <button onClick={() => del(c)} data-testid={`freeze-delete-${c.case_number}`} className="text-red-500 hover:text-red-700 text-xs font-bold inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />{t("common.delete")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(editing || adding) && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { setEditing(null); setAdding(false); }}>
          <form onSubmit={save} className="ec-card max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="freeze-edit-form">
            <h3 className="font-heading font-extrabold text-slate-900">{adding ? t("fz.add") : `${editing.case_number} — ${editing.query}`}</h3>
            {adding && <Field label={t("fz.query")}><input className="ec-input" value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} required data-testid="freeze-query" /></Field>}
            <div className="grid grid-cols-2 gap-2" data-testid="freeze-events">
              {[["deteccion", "fz.ev_deteccion"], ["inmovilizacion", "fz.ev_inmovilizacion"], ["reporte_autoridad", "fz.ev_reporte"], ["confidencialidad", "fz.ev_confidencialidad"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`freeze-ev-${k}`} />
                  {t(label)}
                </label>
              ))}
            </div>
            <Field label={t("fz.action")}><input className="ec-input" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="freeze-action" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("fz.action_date")}><DateInput className="ec-input" value={form.action_date} onChange={(e) => setForm({ ...form, action_date: e.target.value })} data-testid="freeze-action-date" /></Field>
              <Field label={t("fz.status")}>
                <select className="ec-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="freeze-status">
                  <option value="abierto">{t("fz.open")}</option>
                  <option value="documentado">{t("fz.documented")}</option>
                  <option value="cerrado">{t("fz.closed")}</option>
                </select>
              </Field>
            </div>
            <Field label={t("fz.notes")}><textarea className="ec-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="freeze-notes" /></Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEditing(null); setAdding(false); }} className="px-4 py-2 text-sm font-semibold text-slate-600">{t("common.cancel")}</button>
              <button type="submit" disabled={busy} data-testid="freeze-save" className="px-5 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold disabled:opacity-60">{t("common.save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
